// src/pages/MarketplacePage.jsx
// Single public discovery surface — replaces Explore Skills + Explore Playbooks tabs.
// Queries public skills & playbooks directly, enriched with marketplace_listings metadata.
// Free users: FREE_SKILL_LIMIT skills + FREE_PLAYBOOK_LIMIT playbooks.
// Pro users: unlimited.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { detectPlatform } from '../lib/platforms';
import {
  fetchMarketplaceItems, fetchMarketplaceFeatured, fetchMarketplaceCreator,
  submitRating, fetchUserRating, incrementListingView,
  installPlaybook, installSkillFromMarketplace, incrementListingInstall,
  getUserFreeInstallCounts, incrementFreeSkillInstalls, incrementFreePlaybookInstalls,
  FREE_SKILL_LIMIT, FREE_PLAYBOOK_LIMIT,
} from '../lib/supabase';
import { SKILL_CATEGORIES } from '../lib/skills';

const SORT_OPTIONS = [
  { id: 'popular', label: '🔥 Popular' },
  { id: 'newest',  label: '✨ New'     },
  { id: 'rated',   label: '⭐ Top rated' },
];
const TYPE_OPTIONS = [
  { id: 'all',      label: 'All'            },
  { id: 'skill',    label: '⚡ Skills'      },
  { id: 'playbook', label: '📦 Workflows'   },
];
const CAT_COLORS = {
  marketing:'#ef4444', writing:'#8b5cf6', coding:'#3b82f6',
  seo:'#10b981', research:'#06b6d4', creative:'#f59e0b',
  business:'#6366f1', 'social-media':'#ec4899', education:'#14b8a6',
  'image-gen':'#a855f7', 'video-gen':'#f97316', general:'#7c6ffb',
};
function getColor(item) {
  return item.cover_color || CAT_COLORS[item.category] || '#7c6ffb';
}

// ── Stars ──────────────────────────────────────────────────────────────────
function Stars({ rating, count, size = 13, interactive, onRate, userRating }) {
  const [hovered, setHovered] = useState(0);
  const fill = hovered || (interactive ? userRating : rating) || 0;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:3 }}>
      <span style={{ display:'flex', gap:1 }}>
        {[1,2,3,4,5].map(n => (
          <span key={n}
            onClick={interactive ? e => { e.stopPropagation(); onRate(n); } : undefined}
            onMouseEnter={interactive ? () => setHovered(n) : undefined}
            onMouseLeave={interactive ? () => setHovered(0) : undefined}
            style={{ fontSize:size, color: n<=fill ? '#f59e0b':'var(--border2)', cursor: interactive?'pointer':'default', lineHeight:1 }}>
            ★
          </span>
        ))}
      </span>
      {!interactive && count>0 && (
        <span style={{ fontSize:11, color:'var(--dim)' }}>
          {Number(rating).toFixed(1)} ({count})
        </span>
      )}
    </span>
  );
}

// ── Free install badge ─────────────────────────────────────────────────────
function FreeInstallBadge({ skillsLeft, playbooksLeft }) {
  if (skillsLeft <= 0 && playbooksLeft <= 0) return null;
  return (
    <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 12px', borderRadius:20, fontSize:12, background:'var(--accent-dim)', border:'0.5px solid var(--accent-border)', color:'var(--accent)', fontFamily:'var(--font)' }}>
      Free installs remaining:
      {skillsLeft > 0 && <span>⚡ {skillsLeft} skill{skillsLeft!==1?'s':''}</span>}
      {skillsLeft > 0 && playbooksLeft > 0 && <span style={{ color:'var(--border2)' }}>·</span>}
      {playbooksLeft > 0 && <span>📦 {playbooksLeft} playbook{playbooksLeft!==1?'s':''}</span>}
    </div>
  );
}

// ── Item card ──────────────────────────────────────────────────────────────
function ItemCard({ item, onClick, onTagClick }) {
  const isPlaybook = item.item_type === 'playbook';
  const color = getColor(item);
  const catInfo = SKILL_CATEGORIES.find(c => c.id === item.category);
  return (
    <div onClick={onClick} style={{ background:'var(--surface)', border:'0.5px solid var(--border)', borderRadius:14, overflow:'hidden', cursor:'pointer', display:'flex', flexDirection:'column', transition:'border-color 0.14s,transform 0.14s,box-shadow 0.14s', WebkitTapHighlightColor:'transparent' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor=color+'70'; e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow=`0 8px 28px ${color}18`; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='none'; }}>
      {/* Thin colour strip — always present */}
      <div style={{ height:4, background:`linear-gradient(90deg,${color},${color}55)`, flexShrink:0 }} />
      <div style={{ padding:'13px 15px', flex:1, display:'flex', flexDirection:'column', gap:8 }}>
        <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
          {/* Icon slot — thumbnail if available, category icon otherwise */}
          <div style={{ width:44, height:44, borderRadius:10, flexShrink:0, overflow:'hidden', background:`${color}15`, border:`0.5px solid ${color}35`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>
            {item.cover_image_url
              ? <img src={item.cover_image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
              : (isPlaybook ? '📦' : (catInfo?.icon || '⚡'))
            }
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13.5, fontWeight:600, color:'var(--text)', lineHeight:1.3, marginBottom:2, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>{item.title}</div>
            {item.profiles?.username
              ? <Link to={`/marketplace/creator/${item.profiles.username}`} onClick={e => e.stopPropagation()} style={{ fontSize:11, color:'var(--accent)', textDecoration:'none' }}
                  onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                  onMouseLeave={e => e.currentTarget.style.textDecoration='none'}>
                  @{item.profiles.username}
                </Link>
              : <span style={{ fontSize:11, color:'var(--dim)' }}>{item.profiles?.display_name || 'CopyPasteGenius'}</span>
            }
          </div>
        </div>
        {item.description && (
          <div style={{ fontSize:12.5, color:'var(--muted)', lineHeight:1.6, flex:1, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical' }}>{item.description}</div>
        )}
        <div style={{ marginTop:'auto', display:'flex', flexDirection:'column', gap:6 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            {item.rating_count > 0 ? <Stars rating={item.avg_rating} count={item.rating_count} /> : <span style={{ fontSize:11, color:'var(--dim)' }}>No ratings yet</span>}
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              {(item.price_cents||0) > 0
                ? <span style={{ fontSize:11, fontWeight:700, color:'#10b981', background:'rgba(16,185,129,0.1)', border:'0.5px solid rgba(16,185,129,0.3)', borderRadius:6, padding:'1px 7px' }}>${(item.price_cents/100).toFixed(2)}</span>
                : <span style={{ fontSize:10, fontWeight:600, color:'#1d9e75', background:'rgba(29,158,117,0.1)', border:'0.5px solid rgba(29,158,117,0.25)', borderRadius:6, padding:'1px 7px', letterSpacing:'0.03em' }}>FREE</span>
              }
              {item.install_count > 0 && <span style={{ fontSize:11, color:'var(--dim)' }}>⬇ {item.install_count}</span>}
              <button onClick={e => {
                e.stopPropagation();
                const url = `${window.location.origin}/marketplace?item=${item.id}`;
                navigator.clipboard?.writeText(url).catch(() => {});
                e.currentTarget.textContent = '✓';
                setTimeout(() => { if (e.currentTarget) e.currentTarget.textContent = '🔗'; }, 1500);
              }} style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, color:'var(--dim)', padding:0, WebkitTapHighlightColor:'transparent' }} title="Copy share link">🔗</button>
            </div>
          </div>
          <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
            {catInfo && <span style={{ fontSize:10, padding:'2px 7px', borderRadius:20, background:`${color}12`, color, border:`0.5px solid ${color}30` }}>{catInfo.icon} {catInfo.label}</span>}
            <span style={{ fontSize:10, padding:'2px 7px', borderRadius:20, background:'var(--surface2)', color:'var(--dim)', border:'0.5px solid var(--border)' }}>{isPlaybook ? '📦 Playbook' : '⚡ Skill'}</span>
            {item.featured && <span style={{ fontSize:10, padding:'2px 7px', borderRadius:20, background:'rgba(245,158,11,0.1)', color:'#f59e0b', border:'0.5px solid rgba(245,158,11,0.3)' }}>✦ Featured</span>}
          </div>
          {/* Tags */}
          {item.tags?.length > 0 && (
            <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginTop:2 }}>
              {item.tags.slice(0,3).map(t => (
                <button key={t} onClick={e => { e.stopPropagation(); onTagClick?.(t); }}
                  style={{ fontSize:9, padding:'1px 7px', borderRadius:20, background:'var(--surface2)', color:'var(--dim)', border:'0.5px solid var(--border)', cursor:'pointer', fontFamily:'var(--font)', WebkitTapHighlightColor:'transparent' }}>
                  #{t}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Item modal ─────────────────────────────────────────────────────────────
function ItemModal({ item, onClose, onInstalled, freeSkillsLeft, freePlaybooksLeft, onFreeUsed }) {
  const { user, isPro, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [userRating, setUserRating] = useState(null);
  const [, setSubmitting] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [toast, setToast] = useState('');
  const isPlaybook = item.item_type === 'playbook';
  const isPro_ = isPro || isAdmin;
  const color = getColor(item);
  const freeLeft = isPlaybook ? freePlaybooksLeft : freeSkillsLeft;
  const canInstallFree = user && !isPro_ && freeLeft > 0;
  const canInstall = isPro_ || canInstallFree;
  const limitReached = user && !isPro_ && freeLeft <= 0;

  const [fullItem, setFullItem] = useState(item);
  const [hasInstalled, setHasInstalled] = useState(false);

  // isOwner: user created this item
  const isOwner = !!(user && item.user_id && user.id === item.user_id);
  // canSeeFullContent: owner or has previously installed
  const canSeeFullContent = isOwner || installed || hasInstalled;

  useEffect(() => {
    if (item.listing_id) incrementListingView(item.listing_id);
    if (user && item.listing_id) fetchUserRating(item.listing_id, user.id).then(r => r && setUserRating(r.rating));
    const esc = e => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);

    // Fetch full item data (prompt, variables, skills list, etc.)
    const loadFull = async () => {
      try {
        if (item.item_type === 'skill') {
          const { fetchSkillById, hasUserInstalledItem } = await import('../lib/supabase');
          const [full, alreadyInstalled] = await Promise.all([
            fetchSkillById(item.id),
            user ? hasUserInstalledItem('skill', item.id, user.id) : false,
          ]);
          if (full) setFullItem(prev => ({ ...prev, ...full }));
          if (alreadyInstalled) setHasInstalled(true);
        } else if (item.item_type === 'playbook') {
          const { fetchPlaybookById, hasUserInstalledItem } = await import('../lib/supabase');
          const [full, alreadyInstalled] = await Promise.all([
            fetchPlaybookById(item.id),
            user ? hasUserInstalledItem('playbook', item.id, user.id) : false,
          ]);
          if (full) setFullItem(prev => ({ ...prev, ...full }));
          if (alreadyInstalled) setHasInstalled(true);
        }
      } catch(e) { console.warn('Could not load full item:', e.message); }
    };
    loadFull();

    return () => document.removeEventListener('keydown', esc);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRate = async rating => {
    if (!user) { navigate('/auth'); return; }
    if (!item.listing_id) return;
    setSubmitting(true);
    try {
      await submitRating(item.listing_id, user.id, rating);
      setUserRating(rating);
      setToast('Rating saved ✓');
      setTimeout(() => setToast(''), 2000);
    } catch(e) { console.error(e); }
    setSubmitting(false);
  };

  const handleInstall = async () => {
    if (!user) { navigate('/auth'); return; }
    if (!canInstall) { navigate('/premium'); return; }
    if (installing || installed) return;
    setInstalling(true);
    try {
      if (isPlaybook) {
        const { fetchPlaybookById } = await import('../lib/supabase');
        const pb = await fetchPlaybookById(item.id);
        await installPlaybook(pb, user.id);
        if (!isPro_) await incrementFreePlaybookInstalls(user.id);
      } else {
        const { fetchSkillById } = await import('../lib/supabase');
        const sk = await fetchSkillById(item.id);
        await installSkillFromMarketplace(sk, user.id);
        if (!isPro_) await incrementFreeSkillInstalls(user.id);
      }
      if (item.listing_id) await incrementListingInstall(item.listing_id);
      setInstalled(true);
      onFreeUsed?.();
      onInstalled?.();
      setToast(isPlaybook
        ? '📦 Playbook installed! Go to My Playbooks — customize it and publish your own version 🚀'
        : '⚡ Skill added! Use it, tweak it, and publish your own version to the Marketplace 🚀');
    } catch(e) { setToast('Install failed: ' + e.message); }
    setInstalling(false);
    setTimeout(() => setToast(''), 4000);
  };

  const btnLabel = () => {
    if (installing) return 'Installing…';
    if (installed)  return '✓ Installed!';
    if (!user)      return 'Sign in to install free';
    if (limitReached) return `Free limit reached — upgrade to Pro`;
    if (canInstallFree) return `Install free (${freeLeft} left)`;
    return isPlaybook ? '📦 Install playbook' : '⚡ Add to my skills';
  };

  return (
    <div onClick={e => e.target===e.currentTarget && onClose()} style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(4px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
      <div style={{ background:'var(--surface)', borderRadius:'20px 20px 0 0', width:'100%', maxWidth:600, maxHeight:'92dvh', overflow:'auto', boxShadow:'0 -8px 48px rgba(0,0,0,0.4)', animation:'sheetSlideUp 0.24s cubic-bezier(0.32,0.72,0,1) both' }}>
        <div style={{ width:36, height:4, background:'var(--border2)', borderRadius:2, margin:'12px auto 0' }} />

        {/* Cover image hero — shown large when available, colour strip otherwise */}
        {item.cover_image_url
          ? <div style={{ position:'relative', margin:'10px 0 0', height:200, overflow:'hidden', background:`${color}10` }}>
              <img src={item.cover_image_url} alt={item.title}
                style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
              {/* Gradient overlay for title readability */}
              <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 55%)' }} />
              <button onClick={onClose} style={{ position:'absolute', top:10, right:12, background:'rgba(0,0,0,0.45)', border:'none', borderRadius:'50%', width:30, height:30, cursor:'pointer', color:'#fff', fontSize:18, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>×</button>
            </div>
          : <div style={{ height:4, background:`linear-gradient(90deg,${color},${color}55)`, margin:'10px 0 0' }} />
        }

        <div style={{ padding:'20px 22px 36px' }}>
          {/* Header */}
          <div style={{ display:'flex', gap:14, marginBottom:16, alignItems:'flex-start' }}>
            <div style={{ width:48, height:48, borderRadius:12, flexShrink:0, background:`${color}15`, border:`0.5px solid ${color}35`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:24 }}>
              {isPlaybook ? '📦' : (SKILL_CATEGORIES.find(c=>c.id===item.category)?.icon || '⚡')}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:18, fontWeight:700, color:'var(--text)', marginBottom:3 }}>{item.title}</div>
              {item.profiles?.username
                ? <Link to={`/marketplace/creator/${item.profiles.username}`} onClick={onClose} style={{ fontSize:12, color:'var(--accent)', textDecoration:'none' }}>@{item.profiles.username}</Link>
                : <span style={{ fontSize:12, color:'var(--dim)' }}>{item.profiles?.display_name || 'CopyPasteGenius'}</span>
              }
              {item.featured && <span style={{ fontSize:10, color:'#f59e0b', marginLeft:8 }}>✦ Featured</span>}
            </div>
            {!item.cover_image_url && (
              <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--dim)', fontSize:22, padding:4, lineHeight:1, flexShrink:0 }}>×</button>
            )}
          </div>

          {/* Stats */}
          <div style={{ display:'flex', gap:20, padding:'10px 0', borderTop:'0.5px solid var(--border)', borderBottom:'0.5px solid var(--border)', marginBottom:16, alignItems:'center' }}>
            {/* Price — prominent in stats row */}
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:17, fontWeight:700, color: (item.price_cents||0) > 0 ? '#10b981' : '#1d9e75' }}>
                {(item.price_cents||0) > 0 ? `$${(item.price_cents/100).toFixed(2)}` : 'FREE'}
              </div>
              <div style={{ fontSize:10, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Price</div>
            </div>
            {[['Installs', item.install_count||0, 'var(--text)'], ['Views', item.view_count||0, 'var(--text)'], item.rating_count>0 && [item.rating_count+' ratings', Number(item.avg_rating).toFixed(1)+'★', '#f59e0b']].filter(Boolean).map(([label, val, col]) => (
              <div key={label} style={{ textAlign:'center' }}>
                <div style={{ fontSize:17, fontWeight:700, color:col }}>{val}</div>
                <div style={{ fontSize:10, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</div>
              </div>
            ))}
          </div>

          {item.description && <p style={{ fontSize:14, color:'var(--muted)', lineHeight:1.7, margin:'0 0 14px' }}>{item.description}</p>}

          {/* ── Skill details ── */}
          {item.item_type === 'skill' && (
            <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:16 }}>

              {/* Model — always visible */}
              {fullItem.model && (
                <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:'var(--surface2)', borderRadius:8, border:'0.5px solid var(--border)' }}>
                  <span style={{ fontSize:11, color:'var(--dim)', minWidth:52 }}>Model</span>
                  <span style={{ fontSize:12, fontWeight:500, color:'var(--text)', fontFamily:'var(--mono)' }}>{fullItem.model}</span>
                </div>
              )}

              {/* Variables — show count always, names only if owner/installed */}
              {fullItem.variables?.length > 0 && (
                <div style={{ padding:'10px 12px', background:'var(--surface2)', borderRadius:8, border:'0.5px solid var(--border)' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                    <span style={{ fontSize:11, color:'var(--dim)' }}>
                      {fullItem.variables.length} customisable input{fullItem.variables.length !== 1 ? 's' : ''}
                    </span>
                    {!canSeeFullContent && (
                      <span style={{ fontSize:10, color:'var(--dim)', display:'flex', alignItems:'center', gap:4 }}>
                        🔒 Install to see variable names
                      </span>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    {canSeeFullContent
                      ? fullItem.variables.map(v => (
                          <span key={v.name||v} style={{ fontSize:11, padding:'2px 8px', borderRadius:6, background:'var(--accent-dim)', color:'var(--accent)', border:'0.5px solid var(--accent-border)', fontFamily:'var(--mono)' }}>
                            {`{{${v.name||v}}}`}
                          </span>
                        ))
                      : fullItem.variables.map((_, i) => (
                          <span key={i} style={{ fontSize:11, padding:'2px 8px', borderRadius:6, background:'var(--surface)', color:'var(--dim)', border:'0.5px solid var(--border2)', fontFamily:'var(--mono)' }}>
                            {`{{input ${i + 1}}}`}
                          </span>
                        ))
                    }
                  </div>
                </div>
              )}

              {/* Prompt — truncated for non-owners/non-installed, full for owners/installed */}
              {fullItem.prompt && (
                <div style={{ padding:'10px 12px', background:'var(--surface2)', borderRadius:8, border:'0.5px solid var(--border)' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                    <span style={{ fontSize:11, color:'var(--dim)' }}>Prompt preview</span>
                    {!canSeeFullContent && (
                      <span style={{ fontSize:10, color:'var(--dim)' }}>Install to see full prompt</span>
                    )}
                  </div>
                  {canSeeFullContent ? (
                    <div style={{ fontSize:12, color:'var(--muted)', lineHeight:1.65, fontFamily:'var(--mono)', whiteSpace:'pre-wrap', maxHeight:200, overflow:'hidden', maskImage:'linear-gradient(to bottom, black 80%, transparent 100%)', WebkitMaskImage:'linear-gradient(to bottom, black 80%, transparent 100%)' }}>
                      {fullItem.prompt}
                    </div>
                  ) : (
                    <div style={{ position:'relative' }}>
                      {/* Show first 80 chars then blur the rest */}
                      <div style={{ fontSize:12, color:'var(--muted)', lineHeight:1.65, fontFamily:'var(--mono)', whiteSpace:'pre-wrap' }}>
                        {fullItem.prompt.slice(0, 80)}{fullItem.prompt.length > 80 ? '…' : ''}
                      </div>
                      {fullItem.prompt.length > 80 && (
                        <div style={{ marginTop:6, padding:'8px 10px', background:'rgba(0,0,0,0.04)', border:'0.5px dashed var(--border2)', borderRadius:6, textAlign:'center', fontSize:11, color:'var(--dim)' }}>
                          + {Math.max(0, fullItem.prompt.split(' ').length - 14)} more words hidden — install to reveal the full prompt
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Output example text — always visible (it's marketing) */}
              {fullItem.output_example && (
                <div style={{ padding:'10px 12px', background:'var(--surface2)', borderRadius:8, border:'0.5px solid var(--border)' }}>
                  <div style={{ fontSize:11, color:'var(--dim)', marginBottom:6 }}>Example output</div>
                  <div style={{ fontSize:12, color:'var(--muted)', lineHeight:1.65, maxHeight:100, overflow:'hidden', maskImage:'linear-gradient(to bottom, black 50%, transparent 100%)', WebkitMaskImage:'linear-gradient(to bottom, black 50%, transparent 100%)' }}>
                    {fullItem.output_example}
                  </div>
                </div>
              )}

              {/* Output image — always visible (it's marketing) */}
              {fullItem.output_image_url && (
                <div style={{ borderRadius:8, overflow:'hidden', border:'0.5px solid var(--border)', position:'relative' }}>
                  <div style={{ fontSize:11, color:'var(--dim)', padding:'6px 10px', background:'var(--surface2)', borderBottom:'0.5px solid var(--border)' }}>Example output</div>
                  <img src={fullItem.output_image_url} alt="Output example" style={{ width:'100%', display:'block', maxHeight:220, objectFit:'cover' }} />
                  {!canSeeFullContent && (
                    <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'6px 10px', background:'rgba(0,0,0,0.45)', fontSize:10, color:'rgba(255,255,255,0.8)', textAlign:'center' }}>
                      Preview only — install to use
                    </div>
                  )}
                </div>
              )}

              {/* Prompt in use image — always visible */}
              {fullItem.prompt_image_url && (
                <div style={{ borderRadius:8, overflow:'hidden', border:'0.5px solid var(--border)' }}>
                  <div style={{ fontSize:11, color:'var(--dim)', padding:'6px 10px', background:'var(--surface2)', borderBottom:'0.5px solid var(--border)' }}>Prompt in use</div>
                  <img src={fullItem.prompt_image_url} alt="Prompt screenshot" style={{ width:'100%', display:'block', maxHeight:220, objectFit:'cover' }} />
                </div>
              )}
            </div>
          )}

          {/* ── Playbook details ── */}
          {item.item_type === 'playbook' && fullItem.skills && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>
                {fullItem.skills.length} skill{fullItem.skills.length!==1?'s':''} included
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                {fullItem.skills.slice(0, 8).map((sk, i) => {
                  const cat = SKILL_CATEGORIES.find(c => c.id === sk.category);
                  return (
                    <div key={sk.id||i} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', background:'var(--surface2)', borderRadius:8, border:'0.5px solid var(--border)' }}>
                      <span style={{ fontSize:14, flexShrink:0 }}>{cat?.icon || '⚡'}</span>
                      <span style={{ fontSize:12, color:'var(--text)', flex:1 }}>{sk.title}</span>
                      {sk.variables?.length > 0 && (
                        canSeeFullContent
                          ? <span style={{ fontSize:10, color:'var(--dim)' }}>{sk.variables.length} var{sk.variables.length!==1?'s':''}</span>
                          : <span style={{ fontSize:10, color:'var(--dim)' }}>🔒 {sk.variables.length} input{sk.variables.length!==1?'s':''}</span>
                      )}
                      {/* Platform icon */}
                      {sk.model && (() => { const p = detectPlatform(sk.model); return p ? (
                        <span title={p.name} style={{ fontSize:10, padding:'1px 7px', borderRadius:10, background:p.bg||`${p.color}15`, color:p.color, border:`0.5px solid ${p.color}40`, fontWeight:600, flexShrink:0, whiteSpace:'nowrap' }}>
                          {p.name}
                        </span>
                      ) : null; })()}
                    </div>
                  );
                })}
                {fullItem.skills.length > 8 && (
                  <div style={{ fontSize:12, color:'var(--dim)', textAlign:'center', padding:'4px 0' }}>+{fullItem.skills.length - 8} more skills</div>
                )}
                {!canSeeFullContent && (
                  <div style={{ padding:'8px 10px', background:'rgba(0,0,0,0.03)', border:'0.5px dashed var(--border2)', borderRadius:8, textAlign:'center', fontSize:11, color:'var(--dim)', marginTop:4 }}>
                    🔒 Install this playbook to access all prompts and variable names
                  </div>
                )}
              </div>
            </div>
          )}

          {item.tags?.length > 0 && (
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:16 }}>
              {item.tags.map(t => <span key={t} style={{ fontSize:11, padding:'3px 10px', borderRadius:20, background:'var(--surface2)', color:'var(--muted)', border:'0.5px solid var(--border)' }}>{t}</span>)}
            </div>
          )}

          {/* Free limit notice */}
          {user && !isPro_ && (
            <div style={{ padding:'10px 14px', borderRadius:10, marginBottom:14, fontSize:13, background: limitReached ? 'rgba(226,75,74,0.08)' : 'var(--accent-dim)', border:`0.5px solid ${limitReached ? 'rgba(226,75,74,0.25)' : 'var(--accent-border)'}`, color: limitReached ? '#e24b4a' : 'var(--accent)' }}>
              {limitReached
                ? `You've used all ${isPlaybook ? FREE_PLAYBOOK_LIMIT : FREE_SKILL_LIMIT} free ${isPlaybook?'playbook':'skill'} installs. Upgrade for unlimited.`
                : `Free plan: ${freeLeft} ${isPlaybook?'playbook':'skill'} install${freeLeft!==1?'s':''} remaining`
              }
            </div>
          )}

          {/* Install button */}
          <button onClick={handleInstall} disabled={installing}
            style={{ width:'100%', padding:'14px', background: installed ? 'rgba(29,158,117,0.15)' : limitReached ? 'var(--surface2)' : 'var(--accent)', color: installed ? '#1d9e75' : limitReached ? 'var(--muted)' : '#fff', border: installed ? '0.5px solid rgba(29,158,117,0.4)' : limitReached ? '0.5px solid var(--border2)' : 'none', borderRadius:12, fontFamily:'var(--font)', fontSize:15, fontWeight:600, cursor: installing ? 'wait' : 'pointer', transition:'all 0.2s', marginBottom:10 }}>
            {btnLabel()}
          </button>

          {limitReached && <Link to="/premium" style={{ display:'block', textAlign:'center', padding:'12px', background:'var(--accent)', color:'#fff', textDecoration:'none', borderRadius:12, fontFamily:'var(--font)', fontSize:15, fontWeight:600, marginBottom:12 }}>Upgrade to Pro — unlimited installs →</Link>}
          {!user && <div style={{ textAlign:'center', marginBottom:12 }}><Link to="/auth" style={{ fontSize:13, color:'var(--accent)' }}>Sign up free — get {FREE_SKILL_LIMIT} skill + {FREE_PLAYBOOK_LIMIT} playbook installs →</Link></div>}

          {/* Rating + Review */}
          <div style={{ background:'var(--surface2)', borderRadius:10, padding:'12px 14px', marginTop:4 }}>
            <div style={{ fontSize:12, color:'var(--muted)', marginBottom:8 }}>
              {userRating ? `Your rating: ${userRating}/5 — tap to change` : 'Rate & review'}
            </div>
            {item.listing_id
              ? <>
                  <Stars rating={item.avg_rating} count={item.rating_count} size={22} interactive={!!user} onRate={handleRate} userRating={userRating} />
                  {user && userRating > 0 && (
                    <ReviewInput listingId={item.listing_id} userId={user.id} />
                  )}
                </>
              : <span style={{ fontSize:12, color:'var(--dim)' }}>Ratings available once the creator publishes a listing</span>
            }
            {!user && <div style={{ fontSize:11, color:'var(--dim)', marginTop:6 }}><Link to="/auth" style={{ color:'var(--accent)' }}>Sign in</Link> to rate and review</div>}
          </div>
          {/* Reviews list */}
          {item.listing_id && <ReviewsList listingId={item.listing_id} />}

          {toast && <div style={{ marginTop:12, padding:'9px 14px', borderRadius:8, fontSize:13, textAlign:'center', background: toast.includes('failed') ? 'rgba(226,75,74,0.1)' : 'rgba(29,158,117,0.1)', color: toast.includes('failed') ? '#e24b4a' : '#1d9e75', border:`0.5px solid ${toast.includes('failed') ? 'rgba(226,75,74,0.3)' : 'rgba(29,158,117,0.3)'}` }}>{toast}</div>}

          {/* More from this creator */}
          {item.profiles?.username && item.profiles.username !== 'unknown' && (
            <div style={{ marginTop:16, paddingTop:14, borderTop:'0.5px solid var(--border)' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                <span style={{ fontSize:11, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>More from @{item.profiles.username}</span>
                <Link to={`/marketplace/creator/${item.profiles.username}`} onClick={onClose} style={{ fontSize:12, color:'var(--accent)', textDecoration:'none' }}>See all →</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Creator profile ────────────────────────────────────────────────────────
function CreatorProfile({ username, freeSkillsLeft, freePlaybooksLeft, onFreeUsed }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMarketplaceCreator(username).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [username]);

  if (loading) return <div style={{ ...s.page, display:'flex', alignItems:'center', justifyContent:'center' }}><div style={{ color:'var(--dim)', fontSize:13 }}>Loading…</div></div>;
  if (!data?.profile) return (
    <div style={s.page}>
      <div style={{ textAlign:'center', padding:60 }}>
        <div style={{ fontSize:44, marginBottom:12 }}>👤</div>
        <div style={{ color:'var(--muted)', marginBottom:16 }}>Creator not found</div>
        <button onClick={() => navigate('/marketplace')} style={s.btnSecondary}>← Marketplace</button>
      </div>
    </div>
  );

  const { profile, items } = data;
  const totalInstalls = items.reduce((sum, i) => sum + (i.install_count||0), 0);

  return (
    <div style={s.page}>
      <button onClick={() => navigate('/marketplace')} style={{ ...s.btnSecondary, marginBottom:20, alignSelf:'flex-start' }}>← The Library</button>
      <div style={{ display:'flex', gap:16, alignItems:'flex-start', padding:'18px 20px', background:'var(--surface)', borderRadius:16, border:'0.5px solid var(--border)', marginBottom:24 }}>
        <div style={{ width:56, height:56, borderRadius:'50%', flexShrink:0, background:'var(--accent-dim)', border:'2px solid var(--accent-border)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, fontWeight:700, color:'var(--accent)' }}>
          {(profile.display_name||profile.username||'?')[0].toUpperCase()}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:20, fontWeight:700, color:'var(--text)' }}>{profile.display_name||profile.username}</div>
          <div style={{ fontSize:13, color:'var(--accent)', marginBottom:4 }}>@{profile.username}</div>
          {profile.bio && <div style={{ fontSize:13, color:'var(--muted)', lineHeight:1.6 }}>{profile.bio}</div>}
          <div style={{ fontSize:12, color:'var(--dim)', marginTop:6, display:'flex', gap:12 }}>
            <span>{items.length} published</span>
            {totalInstalls > 0 && <span>⬇ {totalInstalls} total installs</span>}
          </div>
        </div>
      </div>
      {items.length === 0
        ? <div style={{ textAlign:'center', padding:40, color:'var(--dim)' }}>No published items yet</div>
        : <div style={s.grid}>{items.map(item => <ItemCard key={item.id} item={item} onClick={() => setSelected(item)} onTagClick={tag => navigate(`/marketplace?tag=${encodeURIComponent(tag)}`)} />)}</div>
      }
      {selected && <ItemModal item={selected} onClose={() => setSelected(null)} freeSkillsLeft={freeSkillsLeft} freePlaybooksLeft={freePlaybooksLeft} onFreeUsed={onFreeUsed} />}
    </div>
  );
}

// ── Review input — shown after user rates ─────────────────────────────────
function ReviewInput({ listingId, userId }) {
  const [text, setText]     = useState('');
  const [saved, setSaved]   = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const { supabase } = await import('../lib/supabase');
      await supabase.from('marketplace_ratings')
        .update({ review: text.trim() })
        .eq('listing_id', listingId)
        .eq('user_id', userId);
      setSaved(true);
    } catch(e) { console.error(e); }
    setSaving(false);
  };

  if (saved) return (
    <div style={{ marginTop:10, fontSize:12, color:'#1d9e75' }}>✓ Review saved — thank you!</div>
  );

  return (
    <div style={{ marginTop:10 }}>
      <textarea
        value={text} onChange={e => setText(e.target.value)}
        placeholder="Share your experience — what worked, what didn't, who it's best for…"
        style={{ width:'100%', background:'var(--bg)', border:'0.5px solid var(--border2)', borderRadius:8, padding:'8px 10px', color:'var(--text)', fontFamily:'var(--font)', fontSize:12, lineHeight:1.6, resize:'vertical', minHeight:72, outline:'none', boxSizing:'border-box' }}
      />
      <button onClick={handleSave} disabled={saving || !text.trim()} style={{ marginTop:6, padding:'6px 14px', background:'var(--accent)', color:'#fff', border:'none', borderRadius:8, fontFamily:'var(--font)', fontSize:12, fontWeight:500, cursor: saving ? 'wait' : 'pointer', opacity: text.trim() ? 1 : 0.5 }}>
        {saving ? 'Saving…' : 'Post review'}
      </button>
    </div>
  );
}

// ── Reviews list — shown at bottom of modal ────────────────────────────────
function ReviewsList({ listingId }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    import('../lib/supabase').then(async ({ supabase }) => {
      const { data } = await supabase
        .from('marketplace_ratings')
        .select('rating, review, created_at, profiles(username, display_name)')
        .eq('listing_id', listingId)
        .not('review', 'is', null)
        .order('created_at', { ascending: false })
        .limit(10);
      setReviews((data || []).filter(r => r.review?.trim()));
      setLoading(false);
    });
  }, [listingId]);

  if (loading || reviews.length === 0) return null;

  return (
    <div style={{ marginTop:16, paddingTop:14, borderTop:'0.5px solid var(--border)' }}>
      <div style={{ fontSize:11, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600, marginBottom:10 }}>
        Reviews ({reviews.length})
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {reviews.map((r, i) => (
          <div key={i} style={{ padding:'10px 12px', background:'var(--surface2)', borderRadius:8, border:'0.5px solid var(--border)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
              <span style={{ fontSize:12, fontWeight:600, color:'var(--text)' }}>
                {r.profiles?.display_name || r.profiles?.username || 'Anonymous'}
              </span>
              <span style={{ display:'flex', gap:1 }}>
                {[1,2,3,4,5].map(n => (
                  <span key={n} style={{ fontSize:11, color: n <= r.rating ? '#f59e0b' : 'var(--border2)' }}>★</span>
                ))}
              </span>
            </div>
            <div style={{ fontSize:12, color:'var(--muted)', lineHeight:1.6 }}>{r.review}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function MarketplacePage({ creatorUsername }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isPro, isAdmin } = useAuth();
  const isPro_ = isPro || isAdmin;

  const [items, setItems]       = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [freeSkillsLeft,    setFreeSkillsLeft]    = useState(FREE_SKILL_LIMIT);
  const [freePlaybooksLeft, setFreePlaybooksLeft] = useState(FREE_PLAYBOOK_LIMIT);

  const category   = searchParams.get('category') || 'all';
  const sort       = searchParams.get('sort')     || 'popular';
  const typeFilter = searchParams.get('type')     || 'all';
  const search     = searchParams.get('q')        || '';
  const tagFilter  = searchParams.get('tag')      || '';
  const [searchInput, setSearchInput] = useState(search);
  const itemParam = searchParams.get('item') || '';

  const loadFreeCounts = useCallback(() => {
    if (user && !isPro_) {
      getUserFreeInstallCounts(user.id).then(c => {
        setFreeSkillsLeft(Math.max(0, FREE_SKILL_LIMIT    - (c.free_skill_installs    || 0)));
        setFreePlaybooksLeft(Math.max(0, FREE_PLAYBOOK_LIMIT - (c.free_playbook_installs || 0)));
      });
    }
  }, [user, isPro_]);

  useEffect(() => { loadFreeCounts(); }, [loadFreeCounts]);

  const setParam = (key, val) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (!val || val === 'all') next.delete(key); else next.set(key, val);
      return next;
    });
  };

  useEffect(() => {
    fetchMarketplaceFeatured().then(setFeatured).catch(console.error);
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMarketplaceItems({ category, sort, search, typeFilter, limit:80 });
      // Client-side tag filter (tags are an array column)
      const filtered = tagFilter
        ? data.filter(item => item.tags?.some(t => t.toLowerCase() === tagFilter.toLowerCase()))
        : data;
      setItems(filtered);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [category, sort, search, typeFilter, tagFilter]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // Auto-open item from share link: /marketplace?item=<id>
  useEffect(() => {
    if (!itemParam || !items.length) return;
    const found = items.find(i => i.id === itemParam);
    if (found) setSelected(found);
  }, [itemParam, items]);

  useEffect(() => {
    const t = setTimeout(() => setParam('q', searchInput), 380);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  if (creatorUsername) return <CreatorProfile username={creatorUsername} freeSkillsLeft={freeSkillsLeft} freePlaybooksLeft={freePlaybooksLeft} onFreeUsed={loadFreeCounts} />;

  const featuredToShow = featured.filter(f => (typeFilter==='all'||f.item_type===typeFilter) && (category==='all'||f.category===category));

  return (
    <div style={s.page}>

      {/* ── Header ── */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>The Library</h1>
          <p style={s.sub}>Discover skills and workflows shared by the community</p>
        </div>
        {isPro_ && (
          <Link to="/skills" style={{ ...s.btnPrimary, textDecoration:'none', gap:6 }}>
            + Share something
          </Link>
        )}
      </div>

      {/* ── Free install badge ── */}
      {user && !isPro_ && (
        <div style={{ marginBottom:14 }}>
          <FreeInstallBadge skillsLeft={freeSkillsLeft} playbooksLeft={freePlaybooksLeft} />
        </div>
      )}

      {/* ── Sign-up prompt ── */}
      {!user && (
        <div style={{ marginBottom:14, padding:'10px 16px', borderRadius:10, background:'var(--accent-dim)', border:'0.5px solid var(--accent-border)', fontSize:13, color:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <span>Sign up free — get {FREE_SKILL_LIMIT} skill installs + {FREE_PLAYBOOK_LIMIT} workflows to try</span>
          <Link to="/auth" style={{ ...s.btnPrimary, textDecoration:'none', fontSize:12, padding:'6px 14px' }}>Sign up →</Link>
        </div>
      )}

      {/* ── Search ── */}
      <div style={{ position:'relative', marginBottom:12 }}>
        <span style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', color:'var(--dim)', fontSize:16, pointerEvents:'none' }}>⌕</span>
        <input
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Search skills, workflows, topics…"
          style={{ ...s.searchInput, paddingLeft:40, fontSize:15 }}
        />
        {searchInput && (
          <button onClick={() => { setSearchInput(''); setParam('q',''); }}
            style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--dim)', fontSize:18, lineHeight:1 }}>×</button>
        )}
      </div>

      {/* ── Single toolbar row: type + sort ── */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, flexWrap:'wrap' }}>
        {/* Type selector */}
        <div style={{ display:'flex', gap:2, background:'var(--surface)', border:'0.5px solid var(--border2)', borderRadius:10, padding:3 }}>
          {TYPE_OPTIONS.map(t => (
            <button key={t.id} onClick={() => setParam('type', t.id)} style={{
              padding:'5px 13px', borderRadius:8, border:'none', cursor:'pointer',
              fontFamily:'var(--font)', fontSize:12.5,
              background: typeFilter===t.id ? 'var(--accent-dim)' : 'none',
              color: typeFilter===t.id ? 'var(--accent)' : 'var(--muted)',
              fontWeight: typeFilter===t.id ? 600 : 400, whiteSpace:'nowrap',
            }}>{t.label}</button>
          ))}
        </div>

        {/* Divider */}
        <div style={{ width:'0.5px', height:24, background:'var(--border2)', flexShrink:0 }} />

        {/* Sort */}
        <div style={{ display:'flex', gap:4 }}>
          {SORT_OPTIONS.map(o => (
            <button key={o.id} onClick={() => setParam('sort', o.id)} style={{
              padding:'5px 12px', borderRadius:8, cursor:'pointer',
              fontFamily:'var(--font)', fontSize:12.5,
              border: sort===o.id ? '1px solid var(--accent-border)' : '0.5px solid var(--border2)',
              background: sort===o.id ? 'var(--accent-dim)' : 'var(--surface)',
              color: sort===o.id ? 'var(--accent)' : 'var(--muted)',
              fontWeight: sort===o.id ? 600 : 400, whiteSpace:'nowrap',
            }}>{o.label}</button>
          ))}
        </div>
      </div>

      {/* ── Category chips ── */}
      <div style={{ display:'flex', gap:7, flexWrap:'nowrap', overflowX:'auto', padding:'2px 0 12px', scrollbarWidth:'none', WebkitOverflowScrolling:'touch', msOverflowStyle:'none' }}>
        {[{ id:'all', label:'All', icon:'' }, ...SKILL_CATEGORIES].map(c => (
          <button key={c.id} onClick={() => setParam('category', c.id)} style={{
            flexShrink:0, padding:'6px 14px', borderRadius:20,
            border: category===c.id ? '1.5px solid var(--accent-border)' : '0.5px solid var(--border2)',
            cursor:'pointer', fontFamily:'var(--font)', fontSize:12, whiteSpace:'nowrap',
            background: category===c.id ? 'var(--accent-dim)' : 'var(--surface)',
            color: category===c.id ? 'var(--accent)' : 'var(--muted)',
            fontWeight: category===c.id ? 600 : 400,
            WebkitTapHighlightColor:'transparent',
            display:'inline-flex', alignItems:'center', gap:4, lineHeight:1,
          }}>
            {c.icon} {c.label}
          </button>
        ))}
      </div>

      {/* ── Featured ── */}
      {featuredToShow.length > 0 && !search && category==='all' && (
        <div style={{ marginBottom:28 }}>
          <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--dim)', marginBottom:10, fontWeight:600 }}>✦ Featured</div>
          <div style={s.grid}>
            {featuredToShow.map(item => (
              <ItemCard key={item.id} item={{ ...item, featured:true }} onClick={() => setSelected(item)} onTagClick={tag => setParam('tag', tag)} />
            ))}
          </div>
          <div style={{ height:1, background:'var(--border)', margin:'22px 0 0' }} />
        </div>
      )}

      {/* ── Active tag filter ── */}
      {tagFilter && (
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
          <span style={{ fontSize:12, color:'var(--muted)' }}>Tag:</span>
          <span style={{ fontSize:12, padding:'3px 10px', borderRadius:20, background:'var(--accent-dim)', color:'var(--accent)', border:'0.5px solid var(--accent-border)', display:'flex', alignItems:'center', gap:6 }}>
            #{tagFilter}
            <button onClick={() => setParam('tag', '')} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--accent)', fontSize:14, lineHeight:1, padding:0 }}>×</button>
          </span>
        </div>
      )}

      {/* ── Results count ── */}
      {!loading && (
        <div style={{ fontSize:12, color:'var(--dim)', marginBottom:14 }}>
          {items.length} {items.length===1?'result':'results'}
          {search && ` for "${search}"`}
          {category!=='all' && ` in ${category}`}
          {tagFilter && ` tagged #${tagFilter}`}
        </div>
      )}

      {/* ── Grid ── */}
      {loading ? (
        <div style={s.grid}>
          {[...Array(6)].map((_,i) => (
            <div key={i} style={{ background:'var(--surface)', border:'0.5px solid var(--border)', borderRadius:14, overflow:'hidden' }}>
              <div style={{ height:4, background:'var(--border2)' }} />
              <div style={{ padding:'16px 18px', display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ display:'flex', gap:12 }}>
                  <div className="skeleton" style={{ width:44, height:44, borderRadius:10, flexShrink:0 }} />
                  <div style={{ flex:1, display:'flex', flexDirection:'column', gap:8 }}>
                    <div className="skeleton" style={{ height:14, width:'70%', borderRadius:4 }} />
                    <div className="skeleton" style={{ height:11, width:'45%', borderRadius:4 }} />
                  </div>
                </div>
                <div className="skeleton" style={{ height:11, width:'95%', borderRadius:4 }} />
                <div className="skeleton" style={{ height:11, width:'75%', borderRadius:4 }} />
                <div className="skeleton" style={{ height:11, width:'55%', borderRadius:4 }} />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 20px' }}>
          <div style={{ fontSize:44, marginBottom:12, opacity:0.3 }}>🔍</div>
          <div style={{ fontSize:16, fontWeight:600, color:'var(--muted)', marginBottom:8 }}>
            {search ? `No results for "${search}"` : 'Nothing shared yet'}
          </div>
          <div style={{ fontSize:13, color:'var(--dim)', marginBottom:24 }}>
            {search ? 'Try different keywords or clear the filters.' : 'Be the first to share something.'}
          </div>
          {search && (
            <button onClick={() => { setSearchInput(''); setParam('q',''); setParam('category','all'); }} style={s.btnSecondary}>
              Clear filters
            </button>
          )}
          {isPro_ && (
            <Link to="/skills" style={{ ...s.btnPrimary, textDecoration:'none', marginLeft:10, display:'inline-flex' }}>
              Share a skill →
            </Link>
          )}
        </div>
      ) : (
        <div style={s.grid}>
          {items.map(item => (
            <ItemCard key={item.id} item={item} onClick={() => setSelected(item)} onTagClick={tag => setParam('tag', tag)} />
          ))}
        </div>
      )}

      {/* ── Upsells ── */}
      {user && !isPro_ && freeSkillsLeft<=0 && freePlaybooksLeft<=0 && (
        <div style={{ marginTop:28, padding:'18px 22px', background:'var(--accent-dim)', border:'0.5px solid var(--accent-border)', borderRadius:16, display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, flexWrap:'wrap' }}>
          <div>
            <div style={{ fontSize:15, fontWeight:600, color:'var(--text)', marginBottom:3 }}>Free installs used up</div>
            <div style={{ fontSize:13, color:'var(--muted)' }}>Upgrade to Pro for unlimited installs, your own skill library, and workflow creation.</div>
          </div>
          <Link to="/premium" style={{ ...s.btnPrimary, textDecoration:'none', flexShrink:0 }}>Upgrade to Pro →</Link>
        </div>
      )}
      {!user && items.length > 0 && (
        <div style={{ marginTop:28, padding:'18px 22px', background:'var(--surface)', border:'0.5px solid var(--border2)', borderRadius:16, display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, flexWrap:'wrap' }}>
          <div>
            <div style={{ fontSize:15, fontWeight:600, color:'var(--text)', marginBottom:3 }}>Install skills and share your own</div>
            <div style={{ fontSize:13, color:'var(--muted)' }}>Free: {FREE_SKILL_LIMIT} skill installs + {FREE_PLAYBOOK_LIMIT} workflows. Pro unlocks everything.</div>
          </div>
          <Link to="/auth" style={{ ...s.btnPrimary, textDecoration:'none', flexShrink:0 }}>Sign up free →</Link>
        </div>
      )}

      {selected && (
        <ItemModal
          item={selected}
          onClose={() => setSelected(null)}
          onInstalled={loadItems}
          freeSkillsLeft={freeSkillsLeft}
          freePlaybooksLeft={freePlaybooksLeft}
          onFreeUsed={loadFreeCounts}
        />
      )}
    </div>
  );
}

const s = {
  page: { background:'var(--bg)', padding:'clamp(14px,3vw,28px)', fontFamily:'var(--font)', display:'flex', flexDirection:'column' },
  header: { display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:18, flexWrap:'wrap' },
  title: { fontSize:'clamp(20px,4vw,26px)', fontWeight:700, color:'var(--text)', margin:0, letterSpacing:'-0.02em' },
  sub: { fontSize:13, color:'var(--muted)', margin:'4px 0 0' },
  grid: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:16 },
  searchInput: { width:'100%', background:'var(--surface)', border:'0.5px solid var(--border2)', borderRadius:10, padding:'11px 14px', color:'var(--text)', fontFamily:'var(--font)', fontSize:14, outline:'none', boxSizing:'border-box', transition:'border-color 0.15s' },
  btnPrimary: { background:'var(--accent)', color:'#fff', border:'none', borderRadius:8, padding:'9px 18px', fontFamily:'var(--font)', fontSize:13, fontWeight:500, cursor:'pointer', display:'inline-flex', alignItems:'center' },
  btnSecondary: { background:'none', color:'var(--muted)', border:'0.5px solid var(--border2)', borderRadius:8, padding:'7px 14px', fontFamily:'var(--font)', fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center' },
};
